import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { deckKeys, reviewQueueKey } from '@/entities/deck';
import { learningKeys } from '@/entities/learning';
import { createCard, deleteCard, updateCard } from '@/shared/api/decks';
import { getLeeches, resetCard } from '@/shared/api/learning';
import type { Leech } from '@/shared/api/types';
import { shareOf } from '@/shared/lib/share';
import { Button, ErrorMessage, TextArea } from '@/shared/ui';

import { looksCompound } from '../lib/looksCompound';
import styles from './Leeches.module.css';

type Mode = 'view' | 'rephrase' | 'split';

interface Draft {
  front: string;
  back: string;
}

function minutesOf(seconds: number): string {
  return seconds > 0 && seconds < 60 ? '< 1' : String(Math.round(seconds / 60));
}

function DraftFields({
  draft,
  onChange,
  legend,
  focus = false,
}: {
  draft: Draft;
  onChange: (draft: Draft) => void;
  legend: string;
  focus?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const front = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focus) {
      front.current?.focus();
    }
  }, [focus]);

  return (
    <fieldset className={styles.fieldset}>
      <legend>{legend}</legend>
      <TextArea
        ref={front}
        label={t('decks.front')}
        required
        maxLength={10000}
        value={draft.front}
        onChange={(event) => {
          const { value } = event.target;
          onChange({ ...draft, front: value });
        }}
      />
      <TextArea
        label={t('decks.back')}
        required
        maxLength={10000}
        value={draft.back}
        onChange={(event) => {
          const { value } = event.target;
          onChange({ ...draft, back: value });
        }}
      />
    </fieldset>
  );
}

function LeechItem({ leech }: { leech: Leech }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { card } = leech;
  const [mode, setMode] = useState<Mode>('view');
  const [first, setFirst] = useState<Draft>({ front: card.front, back: card.back });
  const [second, setSecond] = useState<Draft>({ front: '', back: '' });
  const created = useRef(false);
  const saving = useRef(false);

  const refresh = (): Promise<unknown> =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: learningKeys.all }),
      queryClient.invalidateQueries({ queryKey: deckKeys.one(card.deck_id) }),
      queryClient.invalidateQueries({ queryKey: deckKeys.all, exact: true }),
      queryClient.invalidateQueries({ queryKey: reviewQueueKey }),
    ]);

  const keep = (draft: Draft): Parameters<typeof updateCard>[1] => ({
    front: draft.front,
    back: draft.back,
    source_excerpt: card.source_excerpt,
    source_url: card.source_url,
  });

  const save = useMutation({
    mutationFn: async (split: boolean) => {
      await updateCard(card.id, keep(first));
      if (split && !created.current) {
        await createCard(card.deck_id, keep(second));
        created.current = true;
      }
      await resetCard(card.id);
    },
    onSuccess: refresh,
    onSettled: () => {
      saving.current = false;
    },
  });

  const archive = useMutation({
    mutationFn: () => deleteCard(card.id),
    onSuccess: refresh,
  });

  const open = (next: Mode): void => {
    setFirst({ front: card.front, back: card.back });
    setSecond({ front: '', back: '' });
    created.current = false;
    save.reset();
    setMode(next);
  };

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (saving.current) {
      return;
    }

    saving.current = true;
    save.mutate(mode === 'split');
  };

  const compound = looksCompound(card.back);
  const labelId = `leech-${card.id}`;

  return (
    <li className={styles.item}>
      <div className={styles.content}>
        <p id={labelId} className={styles.text}>
          {card.front}
        </p>
        <p className={[styles.text, styles.muted].join(' ')}>{card.back}</p>
      </div>
      <p className={styles.stats}>
        {[
          ...(leech.lapses > 0 ? [t('health.lapses', { count: leech.lapses })] : []),
          t('health.again', { again: leech.again, reviews: leech.reviews }),
          t('health.time', { minutes: minutesOf(leech.seconds) }),
        ].join(' · ')}
      </p>
      {compound && <p className={styles.hint}>{t('health.compound')}</p>}

      {mode === 'view' ? (
        <div className={styles.actions}>
          <Button
            aria-describedby={labelId}
            onClick={() => {
              open('rephrase');
            }}
          >
            {t('health.rephrase')}
          </Button>
          <Button
            aria-describedby={labelId}
            variant={compound ? 'primary' : 'default'}
            onClick={() => {
              open('split');
            }}
          >
            {t('health.split')}
          </Button>
          <Button
            aria-describedby={labelId}
            variant="danger"
            disabled={archive.isPending}
            onClick={() => {
              if (window.confirm(t('health.archiveConfirm'))) {
                archive.mutate();
              }
            }}
          >
            {t('health.archive')}
          </Button>
        </div>
      ) : (
        <form className={styles.form} onSubmit={submit} aria-label={t(`health.${mode}`)}>
          <DraftFields
            focus
            draft={first}
            onChange={setFirst}
            legend={mode === 'split' ? t('health.firstCard') : t('health.rephrase')}
          />
          {mode === 'split' && (
            <DraftFields draft={second} onChange={setSecond} legend={t('health.secondCard')} />
          )}
          <p className={styles.muted}>{t('health.restartHint')}</p>
          {save.isError && <ErrorMessage error={save.error} />}
          <div className={styles.actions}>
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {t('health.save')}
            </Button>
            <Button
              disabled={save.isPending}
              onClick={() => {
                setMode('view');
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      )}
      {archive.isError && <ErrorMessage error={archive.error} />}
    </li>
  );
}

/**
 * The cards that keep being forgotten, of one deck or of all, with the share
 * of study time they take and a way to fix each: rephrase it, split it in two
 * or put it away. A rephrased or split card is learned again from scratch.
 */
export function LeechList({ deckId }: { deckId?: string }): React.JSX.Element {
  const { t } = useTranslation();
  const report = useQuery({
    queryKey: learningKeys.leeches(deckId),
    queryFn: ({ signal }) => getLeeches({ ...(deckId ? { deckId } : {}), signal }),
    staleTime: 0,
  });

  if (report.isError) {
    return <ErrorMessage error={report.error} />;
  }

  if (!report.data) {
    return <p className={styles.muted}>{t('common.loading')}</p>;
  }

  const { leeches, leech_seconds: leechSeconds, total_seconds: totalSeconds } = report.data;

  if (leeches.length === 0) {
    return <p className={styles.muted}>{t('health.none')}</p>;
  }

  return (
    <div className={styles.list}>
      <p className={styles.summary}>
        {t('health.summary', {
          count: leeches.length,
          share: shareOf(leechSeconds, totalSeconds),
        })}
      </p>
      <ul className={styles.items}>
        {leeches.map((leech) => (
          <LeechItem key={leech.card.id} leech={leech} />
        ))}
      </ul>
    </div>
  );
}
